import * as React from 'react'

import { CheckIcon } from 'lucide-react'
import { classList } from 'react-classlist-helper'

import { HIERARCHY_SEPARATOR } from '~/client/src/shared/components/HierarchyChains'
import { ResourceCard } from '~/client/src/shared/components/ResourceCardList/ResourceCard'
import { buildResourceCardModel } from '~/client/src/shared/components/ResourceCardList/resourceCardMapper'
import { IResourceProperty } from '~/client/src/shared/components/ResourceCardList/types'
import { Checkbox } from '~/client/src/shared/components/shadcn-ui/checkbox'
import WarningNote from '~/client/src/shared/components/WarningNote/WarningNote'
import { ResourceTypeId } from '~/client/src/shared/constants/resourceTypes'
import { useLocationLbs } from '~/client/src/shared/hooks/useLocationSubtitle'
import {
  ResourceCardSchema,
  useResourceCardSchema,
} from '~/client/src/shared/hooks/useResourceCardSchema'
import { useResourceRuntimeContext } from '~/client/src/shared/hooks/useResourceRuntimeContext'
import { IResourceRecord } from '~/client/src/shared/resources/definitions/types'
import { renderResourceRecordIcon } from '~/client/src/shared/resources/icons/resourceRecordIcon'
import { renderResourceSemanticIcon } from '~/client/src/shared/resources/icons/resourceSemanticIcons'
import { getResourceIconKey } from '~/client/src/shared/resources/icons/resourceTypeIcons'

import { formatLocationSubtitle } from '../HierarchyChains'
import { useResourcePickerContext } from './ResourcePickerContext'

export const RESOURCE_ICON_CLASS_NAME =
  'tw:block tw:size-full tw:text-muted-foreground'

export function renderResourceTypeIcon(resourceTypeId: string) {
  const iconKey = getResourceIconKey(resourceTypeId)
  if (!iconKey) return null
  return renderResourceSemanticIcon(iconKey, RESOURCE_ICON_CLASS_NAME)
}

/**
 * Hook that returns the semantic icon for a resource type.
 */
export function useResourceIcon(resourceTypeId: string) {
  return React.useMemo(
    () => renderResourceTypeIcon(resourceTypeId),
    [resourceTypeId],
  )
}

interface IResourcePickerCardProps {
  record: IResourceRecord
  resourceTypeId: string
  icon: React.ReactNode
  cardConfiguration: {
    subtitleProperty: string | null
    displayProperties: string[]
  }
  properties: IResourceProperty[]
  getDisplayValue: (propertyName: string, value: unknown) => string
}

/**
 * Card component used inside ResourcePicker lists.
 * Handles selection state via ResourcePickerContext.
 */
export const ResourcePickerCard: React.FC<IResourcePickerCardProps> =
  React.memo(
    ({
      record,
      resourceTypeId,
      icon,
      cardConfiguration,
      properties,
      getDisplayValue,
    }) => {
      const { selectedIds, mode, pickItem, isItemDisabled, getNote } =
        useResourcePickerContext()
      const isSelected = selectedIds.has(record.id)
      const isMulti = mode === 'multi'
      const isDisabled = !isSelected && !!isItemDisabled?.(record.id)
      const note = getNote?.(record.id)

      const handleClick = React.useCallback(() => {
        if (isDisabled) return
        pickItem(record.id)
      }, [isDisabled, pickItem, record.id])

      // A location row's subtitle is its code, then its LBS.
      const getLocationLbs = useLocationLbs(HIERARCHY_SEPARATOR)
      const isLocation = resourceTypeId === ResourceTypeId.LOCATIONS

      const model = React.useMemo(
        () =>
          buildResourceCardModel({
            record,
            resourceTypeId,
            cardConfiguration,
            properties,
            getDisplayValue,
            resolveSubtitle: ({ record: item, subtitle }) =>
              isLocation
                ? formatLocationSubtitle(
                    item.stxResourceId,
                    getLocationLbs(item),
                  )
                : subtitle,
          }),
        [
          record,
          resourceTypeId,
          cardConfiguration,
          properties,
          getDisplayValue,
          isLocation,
          getLocationLbs,
        ],
      )

      const leadingIcon = renderResourceRecordIcon(
        resourceTypeId,
        record,
        icon,
        RESOURCE_ICON_CLASS_NAME,
      )

      return (
        <div
          className={classList({
            'tw:rounded-md tw:transition-colors': true,
            'tw:hover:bg-accent-soft': !isDisabled,
            'tw:bg-accent-soft': isSelected,
            'tw:opacity-50 tw:cursor-not-allowed': isDisabled,
          })}
        >
          <ResourceCard.Standard
            model={model}
            variant="menu"
            // One listbox option per row; the checkbox below only mirrors its state.
            role="option"
            ariaSelected={isSelected}
            ariaDisabled={isDisabled}
            onClick={isDisabled ? undefined : handleClick}
            leading={leadingIcon}
            selection={
              !isMulti ? undefined : isDisabled ? (
                <span className="tw:size-4" />
              ) : (
                <Checkbox
                  checked={isSelected}
                  displayOnly={true}
                  aria-hidden={true}
                  tabIndex={-1}
                />
              )
            }
            trailing={
              isMulti ? undefined : (
                <CheckIcon
                  aria-hidden="true"
                  className={classList({
                    'tw:size-4': true,
                    'tw:invisible': !isSelected,
                  })}
                />
              )
            }
            footer={note && <WarningNote note={note} className="tw:mt-1" />}
          />
        </div>
      )
    },
  )

export function useResourcePickerCards(
  resourceTypeId: string,
  schema: ResourceCardSchema,
) {
  const runtimeContext = useResourceRuntimeContext()
  const {
    cardConfiguration,
    properties,
    propertiesReady,
    ready,
    getDisplayValue,
  } = useResourceCardSchema({ resourceTypeId, runtimeContext, schema })
  const icon = useResourceIcon(resourceTypeId)

  const renderItem = React.useCallback(
    (record: IResourceRecord) => (
      <ResourcePickerCard
        record={record}
        resourceTypeId={resourceTypeId}
        icon={icon}
        cardConfiguration={cardConfiguration}
        properties={properties}
        getDisplayValue={getDisplayValue}
      />
    ),
    [resourceTypeId, icon, cardConfiguration, properties, getDisplayValue],
  )

  return { properties, propertiesReady, ready, renderItem }
}
